import { kebabCase, startCase } from 'es-toolkit';

import { upperFirstEveryWord } from '@zg-rentals/rental-manager-env';

import numberUtils from '../../srcLegacy/shared/utils/numberUtils';
import stringHelper from '../../srcLegacy/shared/utils/stringHelper';

import PropertyConstants from '../constants/PropertyConstants';

import type {
  GetUserPropertyDetailsAmenitiesGroupWebDto,
  GetUserPropertyDetailsFloorPlanWebDto,
  GetUserPropertyDetailsListingTypeWebDto,
  GetUserPropertyDetailsPetsAllowedWebDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

const propertyUtils = {
  formatAddress(
    addressComponents: Partial<{
      street: string;
      city: string;
      state: string;
      unit: string;
      name: string;
      zip: string;
    }>,
  ) {
    const { street, city, state, unit, name, zip } = addressComponents;

    // Transformations
    let formattedStreet = street ? upperFirstEveryWord(street) : '';
    const formattedCity = city ? startCase(city) : '';
    const formattedState = state ? state.toUpperCase() : '';
    const formattedUnit = unit ? `#${unit.toUpperCase()}` : '';
    const formattedName = name ? upperFirstEveryWord(name) : '';

    const formattedAddressWithoutUnit = [formattedStreet, formattedCity, `${formattedState} ${zip}`]
      .filter((val) => val)
      .join(', ');

    if (unit && unit.length) {
      formattedStreet += ` ${formattedUnit}`;
    }

    const formattedAddress = [formattedStreet, formattedCity, formattedState].filter((val) => val).join(', ');
    const formattedCityState = [formattedCity, formattedState].filter((val) => val).join(', ');
    const formattedCityStateZip = `${formattedCityState} ${zip}`;
    const formattedCompleteAddress = [`${formattedStreet},`, formattedCityState, zip].filter((val) => val).join(' ');

    // Remove ordinal suffixes, apply kebabCase, then add them back
    const withoutOrdinals = formattedAddressWithoutUnit.replace(/(\d+)(st|nd|rd|th)/gi, '$1');
    const formattedKebobCase = kebabCase(withoutOrdinals).replace(/(\d+)(?=-|$)/g, (match) => {
      const original = formattedAddressWithoutUnit.match(new RegExp(`${match}(st|nd|rd|th)`, 'i'));
      return original ? original[0].toLowerCase() : match;
    });

    return {
      formattedAddressWithoutUnit,
      formattedAddress,
      formattedCityState,
      formattedCityStateZip,
      formattedCompleteAddress,
      formattedName,
      formattedStreet,
      formattedKebobCase,
    };
  },
  isRoomForRent({ listing }: { listing?: { listingType?: GetUserPropertyDetailsListingTypeWebDto } }) {
    return listing?.listingType?.code === PropertyConstants.LISTING_CODES_BY_TYPE[PropertyConstants.LISTING_TYPES.ROOM];
  },
  formatFloorPlanDisplayPrice(lowPrice: number, highPrice: number) {
    const formattedLowPrice = numberUtils.asCurrencyWithDefault(lowPrice, '', '$0,0');
    const formattedHighPrice = numberUtils.asCurrencyWithDefault(highPrice, '', '$0,0');

    switch (true) {
      case !lowPrice && !highPrice:
        return '$ -';
      case lowPrice === highPrice:
        return `${formattedLowPrice}`;
      case Boolean(lowPrice && highPrice):
        return `${formattedLowPrice}-${formattedHighPrice}`;
      default:
        return `${formattedLowPrice || formattedHighPrice}`;
    }
  },
  getActiveFloorPlanOrUnitCount(floorPlans: Array<GetUserPropertyDetailsFloorPlanWebDto>) {
    let activeCount = 0;

    floorPlans.forEach((floorPlan) => {
      floorPlan.units &&
        floorPlan.units.forEach((unit) => {
          if (unit.active) {
            activeCount += 1;
          }
        });

      if (floorPlan?.units?.length === 0 && floorPlan.active) {
        activeCount += 1;
      }
    });

    return activeCount;
  },
  getFloorPlanDescription({
    numBathrooms,
    numBedrooms,
    lowPrice,
    highPrice,
    squareFeet,
    units,
  }: Partial<GetUserPropertyDetailsFloorPlanWebDto>) {
    const activeUnits = units
      ? units.reduce((accumulator: Array<{ lowPrice: number; highPrice: number }>, unit) => {
          if (unit.active) {
            accumulator.push({ lowPrice: unit.lowPrice, highPrice: unit.highPrice });
          }
          return accumulator;
        }, [])
      : [];

    const numActive = activeUnits.length;
    const lowActivePrice = numActive && Math.min(...activeUnits.map((unit) => unit.lowPrice));
    const highActivePrice = numActive && Math.max(...activeUnits.map((unit) => unit.highPrice));

    const displayPrice = this.formatFloorPlanDisplayPrice(
      lowActivePrice || Number(lowPrice),
      highActivePrice || Number(highPrice),
    );
    const displayTextForBaths = !numBathrooms ? '- baths' : `${stringHelper.pluralize(numBathrooms, 'bath', 'baths')}`;
    const displaySqft = squareFeet ? ` | ${squareFeet} sq. ft.` : '';
    const displayBeds = numBedrooms || numBedrooms === 0 ? Number(numBedrooms) : '-';
    const displayTextForBeds = displayBeds === 0 ? 'studio' : `${stringHelper.pluralize(displayBeds, 'bed', 'beds')}`;

    return {
      displayPrice,
      fullDisplayText: `${displayPrice} | ${displayTextForBeds} | ${displayTextForBaths}${displaySqft}`,
    };
  },
  getIsPetsAllowedEmpty(petsAllowed?: Partial<GetUserPropertyDetailsPetsAllowedWebDto>) {
    const { negotiable: _, ...petsAllowedDtos } = petsAllowed ?? {};
    return !Object.values(petsAllowedDtos).some((petsAllowedValue) =>
      typeof petsAllowedValue === 'boolean' ? petsAllowedValue !== false : petsAllowedValue?.allowed !== null,
    );
  },
  checkIsAmenityFieldFulfilled: ({
    amenityField,
  }: {
    amenityField?: Array<GetUserPropertyDetailsAmenitiesGroupWebDto>;
  }) => {
    const requiredArray = amenityField?.filter(({ isRequired }) => isRequired);

    if (!requiredArray?.length) {
      return true;
    }

    return Boolean(
      requiredArray?.some(({ values }) => {
        let fulfilled = false;

        values.forEach(({ isSelected }) => {
          fulfilled = fulfilled || isSelected;
        });

        return fulfilled;
      }),
    );
  },
};

export default propertyUtils;
